import { useMemo, useState } from "react";
import { IndianRupee, Building2, Download } from "lucide-react";
import { POSTS, CURRENT_DA_PCT } from "@/data/salaryPosts";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const CITY_TIERS = [
  { value: "x", label: "X (Metro: Delhi, Mumbai, Bengaluru, Chennai, Kolkata, Hyderabad, Ahmedabad)", hra: 27, ta_factor: "upper" },
  { value: "y", label: "Y (Tier-2: 100+ other major cities)", hra: 18, ta_factor: "upper" },
  { value: "z", label: "Z (Tier-3: all remaining cities)", hra: 9, ta_factor: "lower" },
];

function inr(n) {
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Math.round(n));
}

export default function SalaryCalculator({ defaultPost }) {
  const [postSlug, setPostSlug] = useState(defaultPost || POSTS[0].post_slug);
  const [city, setCity] = useState("x");

  const post = useMemo(() => POSTS.find((p) => p.post_slug === postSlug) || POSTS[0], [postSlug]);
  const tier = CITY_TIERS.find((t) => t.value === city) || CITY_TIERS[0];

  const downloadPdf = () => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const cityLabel = `${tier.value.toUpperCase()} class`;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text("hireds.in — Govt Salary Slip", 40, 50);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text("Computed via 7th CPC pay matrix + current DA + city-tier HRA", 40, 68);
    doc.text(`Generated: ${new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}`, 40, 82);
    doc.setTextColor(20);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text(`Post: ${post.sector} — ${post.post}`, 40, 110);
    doc.setFont("helvetica", "normal");
    doc.text(`Pay Level: ${post.pay_level || "—"}  |  City: ${cityLabel}  |  DA: ${CURRENT_DA_PCT}%`, 40, 128);

    autoTable(doc, {
      startY: 150,
      head: [["Component", "Amount (Rs / month)"]],
      body: [
        ["Basic Pay (7th CPC)", inr(calc.basic)],
        [`Dearness Allowance @ ${CURRENT_DA_PCT}%`, inr(calc.da)],
        [`HRA — ${cityLabel} @ ${tier.hra}%`, inr(calc.hra)],
        ["Transport Allowance + DA on TA", inr(calc.ta + calc.da_on_ta)],
        ...(calc.extras > 0 ? [["Special / Running / Other allowance", inr(calc.extras)]] : []),
        [{ content: "Gross Monthly", styles: { fontStyle: "bold", fillColor: [255, 237, 213] } }, { content: inr(calc.gross), styles: { fontStyle: "bold", halign: "right", fillColor: [255, 237, 213] } }],
        ["– NPS contribution (10% of basic + DA)", `- ${inr(calc.nps)}`],
        ["– Professional tax (state avg)", `- ${inr(calc.prof_tax)}`],
        [{ content: "In-hand (excl. income tax)", styles: { fontStyle: "bold", fillColor: [220, 252, 231] } }, { content: inr(calc.in_hand), styles: { fontStyle: "bold", halign: "right", fillColor: [220, 252, 231] } }],
      ],
      headStyles: { fillColor: [234, 88, 12], textColor: 255, fontStyle: "bold" },
      columnStyles: { 0: { cellWidth: 320 }, 1: { halign: "right", cellWidth: 180 } },
      styles: { fontSize: 11, cellPadding: 8 },
      theme: "grid",
    });

    let y = doc.lastAutoTable.finalY + 18;
    doc.setFontSize(9);
    doc.setTextColor(80);
    doc.text(`Plus: Govt NPS contribution Rs. ${inr(calc.govt_contribution)} (going into your NPS, not in-hand)`, 40, y);
    y += 14;
    doc.text("Excludes income tax (depends on regime & deductions) and any field/risk allowance.", 40, y);

    if (post.notes) {
      y += 22;
      doc.setTextColor(180, 83, 9);
      doc.setFont("helvetica", "bold");
      doc.text("Notes on this post:", 40, y);
      y += 14;
      doc.setFont("helvetica", "normal");
      doc.setTextColor(60);
      const wrapped = doc.splitTextToSize(post.notes, 500);
      doc.text(wrapped, 40, y);
      y += wrapped.length * 13;
    }

    y = Math.max(y, doc.internal.pageSize.getHeight() - 60);
    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.text("This is an indicative computation by hireds.in, not an official pay slip. Cross-check with the actual notification PDF.", 40, y);
    doc.text("hireds.in — Latest Indian Govt Jobs, Admit Cards & Results · https://hireds.in", 40, y + 12);

    const safe = post.post_slug.replace(/[^a-z0-9-]/gi, "-");
    doc.save(`hireds-salary-${safe}-${tier.value}.pdf`);
  };

  const calc = useMemo(() => {
    const basic = post.basic;
    const da = (basic * CURRENT_DA_PCT) / 100;
    const hra = (basic * tier.hra) / 100;
    const ta = tier.ta_factor === "upper" ? post.ta_upper : post.ta_lower;
    const da_on_ta = (ta * CURRENT_DA_PCT) / 100;
    const extras = { x: post.extras_x, y: post.extras_y, z: post.extras_z }[city] || 0;
    const gross = basic + da + hra + ta + da_on_ta + extras;
    const nps = (basic + da) * 0.10;
    const govt_contribution = (basic + da) * 0.14; // shown separately
    const prof_tax = 200; // approx, varies by state
    const in_hand = gross - nps - prof_tax;
    return { basic, da, hra, ta, da_on_ta, extras, gross, nps, govt_contribution, prof_tax, in_hand };
  }, [post, tier, city]);

  return (
    <section
      className="bg-white border border-slate-200 rounded-xl p-5 md:p-7"
      aria-labelledby="salary-h2"
      data-testid="salary-calculator"
    >
      <div className="flex items-center gap-2 mb-1">
        <span className="inline-grid place-items-center w-8 h-8 rounded-md bg-orange-100 text-orange-700">
          <IndianRupee className="w-4 h-4" aria-hidden="true" />
        </span>
        <h2
          id="salary-h2"
          className="text-xl md:text-2xl font-extrabold text-slate-900"
          style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}
        >
          Govt Job Salary-in-Hand Calculator
        </h2>
      </div>
      <p className="text-sm text-slate-600 mb-5 leading-relaxed">
        Real take-home using 7th CPC + current DA ({CURRENT_DA_PCT}%) + city-tier HRA. Updated Mar 2026.
      </p>

      <div className="grid sm:grid-cols-2 gap-4 mb-5">
        <label className="block">
          <span className="text-xs font-bold text-slate-700 mb-1 inline-flex items-center gap-1.5">
            <Building2 className="w-3.5 h-3.5" /> Post
          </span>
          <select
            value={postSlug}
            onChange={(e) => setPostSlug(e.target.value)}
            data-testid="salary-post"
            className="w-full min-h-11 px-3 py-2 border border-slate-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500"
          >
            {POSTS.map((p) => (
              <option key={p.post_slug} value={p.post_slug}>{p.sector} — {p.post}</option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="text-xs font-bold text-slate-700 mb-1 inline-flex items-center gap-1.5">
            City Class (for HRA)
          </span>
          <select
            value={city}
            onChange={(e) => setCity(e.target.value)}
            data-testid="salary-city"
            className="w-full min-h-11 px-3 py-2 border border-slate-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-500"
          >
            {CITY_TIERS.map((t) => (
              <option key={t.value} value={t.value}>{t.label.slice(0, 80)}</option>
            ))}
          </select>
        </label>
      </div>

      {/* RESULT */}
      <div className="rounded-xl border border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50 p-5 mb-5" data-testid="salary-result">
        <div className="text-xs font-bold uppercase tracking-wider text-orange-600 mb-1">In-hand salary</div>
        <div className="text-3xl md:text-4xl font-extrabold text-slate-900" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
          ₹ {inr(calc.in_hand)}<span className="text-base font-semibold text-slate-600"> / month</span>
        </div>
        <div className="text-sm text-slate-700 mt-1">
          Gross ₹ {inr(calc.gross)} – NPS ₹ {inr(calc.nps)} – Prof tax ₹ {inr(calc.prof_tax)}
        </div>
        <div className="text-xs text-slate-500 mt-1">
          Excludes income tax (depends on regime &amp; deductions) and any field/risk allowance.
        </div>
        <button
          type="button"
          onClick={downloadPdf}
          data-testid="salary-download-pdf"
          className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors"
        >
          <Download className="w-4 h-4" aria-hidden="true" /> Download printable salary slip (PDF)
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse" data-testid="salary-breakdown">
          <caption className="sr-only">Salary breakdown for {post.post}</caption>
          <thead>
            <tr className="bg-slate-100">
              <th scope="col" className="text-left px-3 py-2 font-bold text-slate-700">Component</th>
              <th scope="col" className="text-right px-3 py-2 font-bold text-slate-700">Amount (₹/mo)</th>
            </tr>
          </thead>
          <tbody>
            <tr><th scope="row" className="text-left px-3 py-2 text-slate-700 border-t border-slate-200">Basic Pay (7th CPC)</th><td className="text-right px-3 py-2 text-slate-900 font-semibold border-t border-slate-200">{inr(calc.basic)}</td></tr>
            <tr className="bg-slate-50"><th scope="row" className="text-left px-3 py-2 text-slate-700 border-t border-slate-200">Dearness Allowance @ {CURRENT_DA_PCT}%</th><td className="text-right px-3 py-2 text-slate-900 font-semibold border-t border-slate-200">{inr(calc.da)}</td></tr>
            <tr><th scope="row" className="text-left px-3 py-2 text-slate-700 border-t border-slate-200">HRA — {tier.value.toUpperCase()} class @ {tier.hra}%</th><td className="text-right px-3 py-2 text-slate-900 font-semibold border-t border-slate-200">{inr(calc.hra)}</td></tr>
            <tr className="bg-slate-50"><th scope="row" className="text-left px-3 py-2 text-slate-700 border-t border-slate-200">Transport Allowance + DA on TA</th><td className="text-right px-3 py-2 text-slate-900 font-semibold border-t border-slate-200">{inr(calc.ta + calc.da_on_ta)}</td></tr>
            {calc.extras > 0 && (
              <tr><th scope="row" className="text-left px-3 py-2 text-slate-700 border-t border-slate-200">Special / Running / Other allowance</th><td className="text-right px-3 py-2 text-slate-900 font-semibold border-t border-slate-200">{inr(calc.extras)}</td></tr>
            )}
            <tr className="bg-orange-50 font-bold"><th scope="row" className="text-left px-3 py-2 text-slate-900 border-t border-slate-300">Gross Monthly</th><td className="text-right px-3 py-2 text-slate-900 border-t border-slate-300">{inr(calc.gross)}</td></tr>
            <tr><th scope="row" className="text-left px-3 py-2 text-red-700 border-t border-slate-200">– NPS contribution (10% of basic+DA)</th><td className="text-right px-3 py-2 text-red-700 font-semibold border-t border-slate-200">- {inr(calc.nps)}</td></tr>
            <tr className="bg-slate-50"><th scope="row" className="text-left px-3 py-2 text-red-700 border-t border-slate-200">– Professional tax (state avg)</th><td className="text-right px-3 py-2 text-red-700 font-semibold border-t border-slate-200">- {inr(calc.prof_tax)}</td></tr>
            <tr className="bg-green-50 font-extrabold"><th scope="row" className="text-left px-3 py-2 text-green-900 border-t border-slate-300">In-hand (excl. income tax)</th><td className="text-right px-3 py-2 text-green-900 border-t border-slate-300">{inr(calc.in_hand)}</td></tr>
            <tr><td colSpan={2} className="text-[11px] text-slate-500 px-3 py-2 border-t border-slate-100">Plus: Govt NPS contribution {inr(calc.govt_contribution)} (going into your NPS, not in-hand)</td></tr>
          </tbody>
        </table>
      </div>

      {post.notes && (
        <p className="mt-4 text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 leading-relaxed">
          <strong className="text-amber-700">Notes on this post:</strong> {post.notes}
        </p>
      )}

      <p className="mt-4 text-[11px] text-slate-500 leading-relaxed">
        Built using 7th CPC pay matrix + current DA + 12th Bipartite Settlement (banking). Cross-check with the actual notification PDF for component-wise breakdown specific to your post.
      </p>
    </section>
  );
}
